import React, { useContext, useEffect, useState } from "react";
import { IQuotationItemsProps } from "./QuotationItems.types";
import {
  ActionButton,
  IIconProps,
  IObjectWithKey,
  Label,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Selection,
  SelectionMode,
  Separator,
  Stack,
  TextField,
  IBreadcrumbItem,
  IGroup,
  Pivot,
  PivotItem,
  IRenderFunction,
  IDetailsGroupDividerProps,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import {
  CustomSpanStyle,
  TextFieldStyles,
  TextFont14,
  TextGrayFieldStyles,
  stackTokensWithGap10,
  stackTokensWithGap20,
  stackTokensWithGap30,
} from "../QuotationStyle";
import { useLocation, useNavigate } from "react-router-dom";
import { QuotationContext } from "../QuotationContext";
import {
  IBundleItemEntity,
  IQuotationType,
  IResponseMessage,
  QuotationInfoType,
} from "../Quotation.types";
import {
  ADD_ACTION,
  DefaultEssentialBundleDiscount,
  EDIT_ACTION,
  InitialStateOfResponseMessage,
  NEW_ACTION,
  NO,
  YES,
} from "../Common/QuotationData";
import { appInsights } from "../../../services/Logging/AppInsights";
import { QuoteSessionTracker } from "../../../Utils/QuoteSessionTracker";
import {
  Breadcrumb,
  CoherenceDataGrid,
} from "@coherence-design-system/controls";
import { useBoolean } from "@fluentui/react-hooks";
import {
  onQuotationSave,
  resetQuotationForm,
  validateQuotationHeaderFields,
} from "../Common/Utilities/Quotation";
import { BreadcrumbStyle } from "../AddQuotation/AddQuotationStyle";
import { TextFont18 } from "./QuotationItemsStyle";
import { AuthorizedDiscountComponent } from "../Components/AuthorizedDiscountComponent/AuthorizedDiscountComponent";

import "./QuotationItems.css";
import { AddItemPanelComponent } from "../Components/AddItemPanelComponent/AddItemPanelComponent";
import { DeviceType } from "../../../common/enum/DeviceTypes";
import { IExtendedItemEntity } from "../Common/Interfaces/IItemEntity";
import { priceWithCurrency } from "../../../Utils/PriceWithCurrency";
import { LocalStorageContextDefaultValues, useLocalStorageContext } from "../../../Provider/LocalStorageProvider";
import { formattedPrice } from "../../../Utils/FormattedPrice";
export const QuotationItems: React.FunctionComponent<
  IQuotationItemsProps
> = () => {
  const AddIcon: IIconProps = { iconName: "Add" };
  const DeleteIcon: IIconProps = { iconName: "Delete" };
  const EditIcon: IIconProps = { iconName: "Edit" };
  const SaveIcon: IIconProps = { iconName: "Save" };
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [quotationSuccessMessage, setQuotationSuccessMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationFailureMessage, setQuotationFailureMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationWaitMessage, setQuotationWaitMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationWarningMessage, setQuotationWarningMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);

  const { lineItemSelectedPivot, setUser } = useLocalStorageContext();
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const [selectedPivot, setSelectedPivot] = useState<string>(
    quotationInfo?.standaloneItems?.length > 0 && lineItemSelectedPivot === LocalStorageContextDefaultValues.lineItemSelectedPivot
      ? DeviceType.Standalone
      : DeviceType.Bundle
  );
  const { quotationError, setQuotationError } = useContext(
    QuotationContext
  ) as IQuotationType;
  const [editModeData, setEditModeData] = useState(
    quotationInfo.standaloneItems
  );
  const [editModeBundleData, setEditModeBundleData] = useState(
    quotationInfo.bundles
  );
  const [editModeBundleItemData, setEditModeBundleItemData] = useState(
    quotationInfo.bundleItems
  );
  const [
    isItemPanelOpen,
    { setTrue: openItemPanel, setFalse: dismissItemPanel },
  ] = useBoolean(false);
  const [
    isDiscountPanelOpen,
    { setTrue: openDiscountPanel, setFalse: dismissDiscountPanel },
  ] = useBoolean(false);
  const [itemTableLoading, setItemTableLoading] = useState<boolean>(false);
  const [bundleItemLoading, setBundleItemLoading] = useState<boolean>(false);
  const [selectedGridData, setSelectedGridData] = React.useState<
    IObjectWithKey[] | undefined
  >(undefined);
  const [selectedBundleGridData, setSelectedBundleGridData] = React.useState<{
    items: IExtendedItemEntity[];
  }>({ items: [] });
  const [bundle, setBundle] = useState<IGroup[]>([]);

  const resetItemData = () => {
    dismissItemPanel();
  };
  const calculateTotal = () => {
    let netAmountSum = 0.0;
    quotationInfo.standaloneItems.forEach((item) => {
      netAmountSum += Number(parseFloat(item.netAmount.toString()).toFixed(2));
    });
    quotationInfo.bundleItems.forEach((item) => {
      netAmountSum += Number(parseFloat(item.netAmount.toString()).toFixed(2));
    });
    const value =
      netAmountSum > 0 ? netAmountSum?.toFixed(2).toLocaleString() : "";
    setQuotationInfo({
      ...quotationInfo,
      total: value,
    });
  };
  useEffect(() => {
    if (
      selectedPivot === DeviceType.Standalone &&
      quotationInfo.standaloneItems.length === 0
    ) {
      setSelectedPivot(DeviceType.Bundle);
    } else if (lineItemSelectedPivot === DeviceType.Bundle) {
      setSelectedPivot(DeviceType.Bundle);
      setUser((previousUser) => ({
        ...previousUser,
        lineItemSelectedPivot: LocalStorageContextDefaultValues.lineItemSelectedPivot,
      }));
    }
    calculateTotal();
    setEditModeData(quotationInfo.standaloneItems);
    if (quotationInfo?.standaloneItems && quotationInfo?.standaloneItemsClone && JSON.stringify(quotationInfo?.standaloneItems) !== JSON.stringify(quotationInfo?.standaloneItemsClone)) {
      setQuotationWarningMessage({
        showMessage: true,
        message: 'You have some unsaved data in your cart, please click on Calculate & Save to proceed.'
      });
    } else {
      setQuotationWarningMessage(InitialStateOfResponseMessage);
    }
    itemSelection.setAllSelected(false);
    setItemTableLoading(false);
  }, [JSON.stringify(quotationInfo.standaloneItems)]);
  useEffect(() => {
    if (
      selectedPivot === DeviceType.Bundle &&
      quotationInfo.bundleItems.length === 0 &&
      lineItemSelectedPivot !== DeviceType.Bundle
    ) {
      setSelectedPivot(DeviceType.Standalone);
    }
    setBundleItemLoading(true);
    const groups: IGroup[] = quotationInfo.bundles.map(
      (item: IBundleItemEntity) => {
        const { bundleId, bundleTitle, count, startIndex, index } = item;
        const groupItem = {
          key: bundleId.concat(`-${index}`),
          name: bundleTitle,
          startIndex,
          count,
          level: 0,
        };
        return groupItem;
      }
    );
    setBundle(groups);
    setBundleItemLoading(false);
    calculateTotal();
    setEditModeBundleData(quotationInfo.bundles);
    setEditModeBundleItemData(quotationInfo.bundleItems);
    if (quotationInfo?.bundleItems && quotationInfo?.bundleItemsClone && JSON.stringify(quotationInfo?.bundleItems) !== JSON.stringify(quotationInfo?.bundleItemsClone)) {
      setQuotationWarningMessage({
        showMessage: true,
        message: 'You have some unsaved data in your cart, please click on Calculate & Save to proceed.'
      });
    } else {
      setQuotationWarningMessage(InitialStateOfResponseMessage);
    }
    bundleSelection.setAllSelected(false);
    setBundleItemLoading(false);
  }, [JSON.stringify(quotationInfo.bundleItems)]);

  const getBreadcrumbItems = () => {
    const items: IBreadcrumbItem[] = [
      {
        text: "Quotations",
        key: "Quotations",
        onClick: () => {
          navigate("/AllQuotations");
        },
      },
      { text: `${location?.state?.actionName} Quotation`, key: `${location?.state?.actionName} Quotation` },
    ];
    if (quotationInfo?.customer?.accountId) {
      items.push({
        text: `${quotationInfo?.customer?.accountId}`,
        key: `${quotationInfo?.customer?.accountId}`,
        isCurrentItem: true,
      });
    }
    return items;
  };

  const itemSelection = React.useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          appInsights.trackEvent({
            name: "In QuotationItems.tsx, calling selection change on Click checkbox",
            properties: { selection: itemSelection.getSelection() },
          });
          setSelectedGridData(itemSelection.getSelection());
        },
        selectionMode: SelectionMode.single,
      }),
    [selectedPivot, JSON.stringify(quotationInfo.standaloneItems)]
  );
  const bundleSelection = React.useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          const selectedBundle = bundleSelection.getSelection();
          appInsights.trackEvent({
            name: "In QuotationItems.tsx, calling selection change on Click checkbox",
            properties: { selection: selectedBundle },
          });
          setSelectedBundleGridData({
            items: selectedBundle as IExtendedItemEntity[],
          });
        },
        selectionMode: SelectionMode.multiple,
      }),
    [
      JSON.stringify(quotationInfo.bundles),
      JSON.stringify(quotationInfo.bundleItems),
    ]
  );

  const onUpdateStandaloneItem =
    (
      field: keyof IExtendedItemEntity,
      value: IExtendedItemEntity[keyof IExtendedItemEntity],
      index: number
    ) =>
      (prevState: IExtendedItemEntity[]) => {
        if (field === "quantity") {
          return [
            ...prevState.slice(0, index),
            Object.assign({}, prevState[index!], {
              [field]: Number(value),
              netAmount: Number(
                (Number(value) * Number(prevState[index!].msrp)).toFixed(2)
              ),
              unitPrice: prevState[index!].msrp,
              authorizedDiscount: 0.0,
              authDiscountAmounts: [],
              authDiscountPercentages: [],
              discountReason: "",
              discountedPercentage: 0,
              lineItemIndex: index,
            }),
            ...prevState.slice(index! + 1),
          ];
        } else {
          return [
            ...prevState.slice(0, index),
            Object.assign({}, prevState[index!], {
              [field]: value,
            }),
            ...prevState.slice(index! + 1),
          ];
        }
      };

  const _onClickSaveButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setQuotationWarningMessage({
      ...quotationWarningMessage,
      showMessage: false,
    });
    setQuotationFailureMessage(InitialStateOfResponseMessage)
    setIsLoading(true);
    appInsights.trackEvent({
      name: "In QuotationItems.tsx, Clicked save button to trigger create quotation API.",
      properties: { quotationNumber: quotationInfo?.quotationNumber },
    });
    setQuotationWaitMessage({
      showMessage: true,
      message: `Please wait...`,
    });
    validateQuotationHeaderFields(
      location?.state?.actionName,
      quotationError,
      quotationInfo,
      setIsLoading,
      setQuotationError,
      setQuotationFailureMessage,
      true
    )
      .then((isValid) => {
        if (isValid) {
          onQuotationSave(
            location?.state?.actionName,
            navigate,
            quotationInfo,
            setQuotationInfo,
            setQuotationError,
            setIsLoading,
            setQuotationSuccessMessage,
            setQuotationFailureMessage,
            setQuotationWaitMessage,
          );
        } else {
          if (
            quotationInfo.bundleItems.length === 0 &&
            quotationInfo.standaloneItems.length > 0
          ) {
            try {
              const hasEssentialBundleDiscount =
                quotationInfo.standaloneItems.some((item) =>
                  item.authDiscountAmounts?.some(
                    (discount) =>
                      discount.authorizedDiscountReason ===
                      DefaultEssentialBundleDiscount.contractSelected
                  )
                );
              if (hasEssentialBundleDiscount) {
                setQuotationError({
                  ...quotationError,
                  standaloneItems: true,
                });
                const message =
                  location?.state?.actionName === NEW_ACTION
                    ? `Unable to create`
                    : `Unable to update`;
                setQuotationFailureMessage({
                  showMessage: true,
                  message: `${message} quotation with standalone items only having discount ${DefaultEssentialBundleDiscount.contractSelected}. Please either remove ${DefaultEssentialBundleDiscount.contractSelected} discount or add a bundle to proceed.`,
                });
                setIsLoading(false);
              } else {
                if (quotationError.standaloneItems) {
                  setQuotationError({
                    ...quotationError,
                    standaloneItems: false,
                  });
                  setQuotationFailureMessage({
                    showMessage: true,
                    message:
                      location?.state?.actionName === NEW_ACTION
                        ? `Unable to create quotation with some missing or incorrect information.`
                        : `Unable to edit quotation with some missing or incorrect information.`,
                  });
                  setIsLoading(false);
                }
              }
            } catch (error) {
              setQuotationError({
                ...quotationError,
                standaloneItems: true,
              });
              setQuotationFailureMessage({
                showMessage: true,
                message: error as string,
              });
              setIsLoading(false);
            }
          } else if (quotationError.standaloneItems) {
            setQuotationError({
              ...quotationError,
              standaloneItems: false,
            });
          }
          setIsLoading(false);
          setQuotationWaitMessage(InitialStateOfResponseMessage);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setQuotationWaitMessage(InitialStateOfResponseMessage);
        setQuotationFailureMessage({
          showMessage: true,
          message: error,
        });
      });
  };

  const isBundleSelected = (arr: any, start: number, end: number) => {
    const expectedValues = Array.from(
      { length: end - start + 1 },
      (_, index) => start + index
    );
    return expectedValues.every((value) => arr.includes(value));
  };

  const renderCustomCommandBar = () => {
    return (
      <Stack horizontal>
        <Stack horizontal style={{ float: "left" }}>
          <Stack horizontal>
            <ActionButton
              iconProps={AddIcon}
              onClick={() => {
                if (
                  quotationInfo.bundleItems.length +
                  quotationInfo.standaloneItems.length >=
                  25
                ) {
                  setQuotationFailureMessage({
                    showMessage: true,
                    message:
                      "You are not allowed to add more than 25 line items in this quotation",
                  });
                } else {
                  // Capture the end time for search products and apply cart session
                  QuoteSessionTracker.setSearchProductsAndApplyCartEnd();
                  
                  appInsights.trackEvent({
                    name: "In QuotationItems.tsx, Clicked Add Item button to open Item details screen to add item data.",
                    properties: {
                      component: `${location?.state?.actionName} Quotation Items`,
                    },
                  });
                  resetItemData();
                  openItemPanel && openItemPanel();
                }
                setIsEditing(false);
              }}
            >
              Add
            </ActionButton>
            {!isEditing && (
              <ActionButton
                iconProps={EditIcon}
                disabled={
                  (selectedPivot === DeviceType.Standalone &&
                    quotationInfo?.standaloneItems?.length > 0) ||
                    (selectedPivot === DeviceType.Bundle &&
                      quotationInfo?.bundleItems?.length > 0)
                    ? false
                    : true
                }
                onClick={() => {
                  appInsights.trackEvent({
                    name: "In QuotationItems.tsx, Clicked Edit Item button to open Item details screen to edit item data.",
                    properties: { component: `${location?.state?.actionName} Quotation Items` },
                  });
                  setIsEditing(!isEditing);
                }}
              >
                Edit
              </ActionButton>
            )}
            {isEditing && (
              <ActionButton
                iconProps={SaveIcon}
                onClick={() => {
                  if (selectedPivot === DeviceType.Standalone) {
                    setSelectedGridData([]);
                    itemSelection.setAllSelected(false);
                    if (editModeData.every((item) => item.quantity && item.quantity > 0)) {
                      setQuotationInfo({
                        ...quotationInfo,
                        standaloneItems: [...editModeData],
                      });
                    } else {
                      setQuotationFailureMessage({
                        showMessage: true,
                        message:
                          "Unable to Save Edited line items because of invalid quantity.",
                      });
                      setIsLoading(false);
                    }
                  } else if (selectedPivot === DeviceType.Bundle) {
                    setSelectedBundleGridData({ items: [] });
                    bundleSelection.setAllSelected(false);
                    if (
                      editModeBundleData.every((item) => item.quantity && item.quantity > 0) &&
                      editModeBundleItemData.every((item) => item.quantity && item.quantity > 0)
                    ) {
                      setQuotationInfo({
                        ...quotationInfo,
                        bundles: [...editModeBundleData],
                        bundleItems: [...editModeBundleItemData],
                      });
                    } else {
                      setQuotationFailureMessage({
                        showMessage: true,
                        message:
                          "Unable to Save Edited line items because of invalid quantity.",
                      });
                      const updatedEditModeBundleData = editModeBundleData.map(item => ({ ...item, quantity: item.quantity > 0 ? item.quantity : 1 }));
                      setEditModeBundleData(updatedEditModeBundleData);
                      setQuotationInfo({
                        ...quotationInfo,
                        bundles: [...updatedEditModeBundleData],
                        bundleItems: [...editModeBundleItemData],
                      });
                      setIsLoading(false);
                    }
                  }
                  setIsEditing(!isEditing);
                }}
              >
                Save Edited Line Item
              </ActionButton>
            )}
            <ActionButton
              iconProps={DeleteIcon}
              disabled={
                (selectedPivot === DeviceType.Standalone &&
                  selectedGridData?.length! > 0) ||
                  (selectedPivot === DeviceType.Bundle &&
                    selectedBundleGridData?.items?.length! > 0)
                  ? false
                  : true
              }
              onClick={() => {
                appInsights.trackEvent({
                  name: "In QuotationItems.tsx, Clicked Edit Item button to open Item details screen to edit item data.",
                  properties: { component: `${location?.state?.actionName} Quotation Items` },
                });
                if (
                  selectedPivot === DeviceType.Standalone &&
                  selectedGridData?.length
                ) {
                  const { skuId, lineItemIndex } = selectedGridData[0] as IExtendedItemEntity;
                  appInsights.trackEvent({
                    name: "In QuotationItems.tsx, Click Remove Line button to remove selected item.",
                    properties: { itemSku: skuId },
                  });
                  const updatedLineItems = quotationInfo.standaloneItems
                    .filter((q, index) => index !== lineItemIndex)
                    .map((item, index) => ({ ...item, lineItemIndex: index }));
                  setQuotationInfo({
                    ...quotationInfo,
                    standaloneItems: [...updatedLineItems],
                  });
                  itemSelection.setAllSelected(false);
                  setSelectedGridData([]);
                } else if (
                  selectedPivot === DeviceType.Bundle &&
                  selectedBundleGridData?.items?.length
                ) {
                  if (
                    selectedBundleGridData?.items?.length ===
                    quotationInfo.bundleItems.length
                  ) {
                    setQuotationInfo({
                      ...quotationInfo,
                      bundles: [],
                      bundleItems: [],
                    });
                  } else {
                    const isCorrectBundle = quotationInfo.bundles.find(
                      (bundle, index) => {
                        return (
                          bundle.startIndex ===
                          selectedBundleGridData?.items[0].lineItemIndex &&
                          bundle.startIndex + bundle.count - 1 ===
                          selectedBundleGridData?.items[
                            selectedBundleGridData?.items?.length - 1
                          ].lineItemIndex &&
                          selectedBundleGridData.items.length === bundle.count
                        );
                      }
                    );
                    if (isCorrectBundle) {
                      appInsights.trackEvent({
                        name: "In QuotationItems.tsx, Click Remove/Delete Line button to remove selected bundle item and bundle.",
                        properties: {
                          bundleSku: isCorrectBundle.bundleId.concat(
                            isCorrectBundle.index.toString()
                          ),
                        },
                      });
                      const updatedBundle: IBundleItemEntity[] = [];
                      let startIndex = isCorrectBundle.startIndex;
                      quotationInfo.bundles.forEach((bundle) => {
                        if (bundle.index !== isCorrectBundle.index) {
                          updatedBundle.push({
                            ...bundle,
                            startIndex:
                              bundle.index > isCorrectBundle.index
                                ? startIndex
                                : bundle.startIndex,
                            index: updatedBundle.length,
                          });
                          startIndex = bundle.startIndex + bundle.count;
                        }
                      });
                      const updatedBundleItems: IExtendedItemEntity[] = [];
                      quotationInfo.bundleItems.forEach((item) => {
                        if (
                          item.lineItemIndex >=
                          isCorrectBundle.count + isCorrectBundle.startIndex
                        ) {
                          updatedBundleItems.push({
                            ...item,
                            lineItemIndex: updatedBundleItems.length,
                          });
                        } else if (
                          item.lineItemIndex < isCorrectBundle.startIndex
                        ) {
                          updatedBundleItems.push(item);
                        }
                      });
                      setQuotationInfo({
                        ...quotationInfo,
                        bundleItems: updatedBundleItems,
                        bundles: updatedBundle,
                      });
                      setQuotationSuccessMessage({
                        showMessage: true,
                        message:
                          "You had deleted the selected bundle successfully",
                      });
                    } else {
                      appInsights.trackEvent({
                        name: "In QuotationItems.tsx, Click Remove/Delete does not allow to delete/remove selected bundle item and bundle.",
                      });
                      setQuotationFailureMessage({
                        showMessage: true,
                        message:
                          "You need to select the correct bundle to delete from line items.",
                      });
                    }
                  }
                  bundleSelection.setAllSelected(false);
                  setSelectedBundleGridData({ items: [] });
                }
              }}
            >
              Delete
            </ActionButton>
            <ActionButton
              iconProps={EditIcon}
              disabled={
                (selectedPivot === DeviceType.Standalone &&
                  selectedGridData?.length! === 1) ||
                  (selectedPivot === DeviceType.Bundle &&
                    selectedBundleGridData?.items?.length! === 1)
                  ? false
                  : true
              }
              onClick={() => {
                openDiscountPanel && openDiscountPanel();
              }}
            >
              Authorized Discount
            </ActionButton>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const onRenderDetailsHeader = (headerProps: any, defaultRender: any) => {
    if (!headerProps || !defaultRender) {
      return null;
    }
    return defaultRender({
      ...headerProps,
      styles: {
        root: {
          height: "70px",
          lineHeight: "70px",
          paddingBottom: "10px",
          selectors: {
            ".ms-DetailsHeader-cell": {
              whiteSpace: "normal",
              textOverflow: "clip",
              lineHeight: "normal",
              height: "60px",
              paddingBottom: "10px",
            },
            ".ms-DetailsHeader-cellTitle": {
              height: "120%",
              alignItems: "center",
            },
          },
        },
      },
    });
  };

  const renderCustomRow = (props: any, defaultRender: any) => {
    const { quantity, availableInventory, isMPriceCalculated } = props.item;
    const rowText =
      quantity > availableInventory
        ? `Alert: Quantity is greater than Available Inventory.`
        : "";
    const warningMessage = !isMPriceCalculated ? `MSRP Ex ${props.item.taxType} not available for this item.` : '';
    return (
      <Stack>
        {rowText && (
          <div role="none" className="waringMessage" style={{ display: 'flex', alignItems: 'center', padding: '4px 12px', background: '#f3f2f1' }}>
            <span style={{ marginRight: 8, fontSize: 16 }} aria-hidden="true">ℹ</span>
            <span>{rowText}</span>
          </div>
        )}
        {warningMessage && (
          <div role="none" style={{ display: 'flex', alignItems: 'center', padding: '4px 12px', background: '#fff4ce' }}>
            <span style={{ marginRight: 8, fontSize: 16 }} aria-hidden="true">⚠</span>
            <span>{warningMessage}</span>
          </div>
        )}
        {defaultRender({
          ...props,
        })}
      </Stack>
    );
  };

  const renderStandaloneItemDetailsComponent = (): React.ReactElement => {
    return (
      <Stack>
        <div
          style={{
            height: "350px",
          }}
        >
          <CoherenceDataGrid
            ariaLabelForSelectAllCheckbox="select all checkbox"
            checkButtonAriaLabel="checkbox"
            compactMode="compact"
            data={quotationInfo.standaloneItems}
            onRenderDetailsHeader={onRenderDetailsHeader}
            isLoading={itemTableLoading}
            onRenderRow={renderCustomRow}
            isScrollable
            listConfig={[
              {
                fieldName: "sapItemId",
                maxWidth: 100,
                isResizable: true,
                key: "sapItemId",
                name: "SKU",
                type: "string",
              },
              {
                fieldName: "productTitle",
                isResizable: true,
                isMultiline: true,
                key: "productName",
                name: "Product Name",
                type: "string",
              },
              {
                fieldName: "availableInventory",
                isResizable: true,
                key: "availableInventory",
                name: "Available Inventory",
                type: "number",
                minColumnWidth: 150,
                customFooterTitle: "",
                showTotal: false,
              },
              {
                fieldName: "quantity",
                isResizable: true,
                key: "quantity",
                name: "Quantity",
                type: "number",
                isUpdated: true,
                isMultiline: true,
                displayFullValue: true,
                ...(isEditing && selectedPivot === DeviceType.Standalone
                  ? {
                    onRender: (
                      item: IExtendedItemEntity,
                      index: number | undefined
                    ) => {
                      return (
                        <TextField
                          value={item.quantity.toString()}
                          ariaLabel="Quantity"
                          errorMessage={
                            item.quantity <= 0 ? "Invalid Quantity" : ""
                          }
                          type="number"
                          onChange={(
                            event: React.FormEvent<
                              HTMLInputElement | HTMLTextAreaElement
                            >,
                            newValue: string | undefined
                          ) => {
                            const updatedData = onUpdateStandaloneItem(
                              "quantity",
                              newValue!,
                              index!
                            );
                            if (typeof updatedData === 'function') {
                              setEditModeData((prevData) => {
                                const newData = updatedData(prevData);

                                if (newData && newData[index!]) {
                                  const updatedStandaloneItems = quotationInfo.standaloneItems.map((item, idx) => {
                                    if (idx === index) {
                                      return newData[index!];
                                    }
                                    return item;
                                  });

                                  setQuotationInfo({
                                    ...quotationInfo,
                                    standaloneItems: updatedStandaloneItems
                                  });
                                }

                                return newData;
                              });
                            } else {
                              setEditModeData(updatedData);

                              if (updatedData && updatedData[index!]) {
                                const updatedStandaloneItems = quotationInfo.standaloneItems.map((item, idx) => {
                                  if (idx === index) {
                                    return updatedData[index!];
                                  }
                                  return item;
                                });

                                setQuotationInfo({
                                  ...quotationInfo,
                                  standaloneItems: updatedStandaloneItems
                                });
                              }
                            }
                            if (parseInt(newValue!) <= 0) {
                              setQuotationError({
                                ...quotationError,
                                lineItems: {
                                  ...quotationError.lineItems,
                                  quantity: true,
                                },
                              });
                            } else {
                              setQuotationError({
                                ...quotationError,
                                lineItems: {
                                  ...quotationError.lineItems,
                                  quantity: false,
                                },
                              });
                            }
                          }}
                        />
                      );
                    },
                  }
                  : {}),
                customFooterTitle: "",
              },
              {
                fieldName: "discountedPercentage",
                isResizable: true,
                key: "Discount Percentage",
                name: "Discount Percentage",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                minColumnWidth: 150,
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {item.discountedPercentage}%
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: "isPreOrder",
                isResizable: true,
                key: "Is Pre Order",
                name: "Is Pre Order",
                type: "string",
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {item.isPreOrder ? YES : NO}
                      </span>
                    </Stack>
                  );
                },
              },
              {
                fieldName: "msrp",
                isResizable: true,
                key: "MSRP",
                name: quotationInfo.standaloneItems[0].taxType ? `MSRP Ex ${quotationInfo.standaloneItems[0].taxType}` : "MSRP",
                type: "number",
                showTotal: false,
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.msrp)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: "authorizedDiscount",
                isResizable: true,
                key: "Authorized Discount Amount",
                name: "Authorized Discount Amount",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                minColumnWidth: 200,
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.authorizedDiscount)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: "unitPrice",
                isResizable: true,
                key: "Unit Best Price",
                name: "Unit Best Price",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.unitPrice)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: `netAmount`,
                isResizable: true,
                key: "Best Price",
                name: "Best Price",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.netAmount)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: `discountReason`,
                isResizable: true,
                isMultiline: true,
                key: "Discount Reason",
                name: "Discount Reason",
                type: "string",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {item.discountReason && item.discountReason.length > 0
                          ? item.discountReason
                          : "--"}
                      </span>
                    </Stack>
                  );
                },
              },
            ]}
            selection={itemSelection}
            selectionMode={SelectionMode.single}
            totalsRowTitleSettings={{
              title: "Total",
              tooltipMessage: "All total will appear here",
            }}
            setKey="STANDALONE_ITEM_DETAILS_DATA_GRID_SORTABLE_SELECTABLE"
          />
        </div>
      </Stack>
    );
  };

  const onRenderGroupHeader: IRenderFunction<IDetailsGroupDividerProps> = (
    props,
    defaultRender
  ) => {
    if (
      props?.groupIndex !== undefined &&
      props?.groupIndex >= 0 &&
      editModeBundleData[props?.groupIndex!]
    ) {
      const { startIndex, count, quantity } =
        editModeBundleData[props?.groupIndex!];
      const customInputOnChange = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
      ) => {
        if (Number(newValue) <= 0) {
          const updatedBundles = editModeBundleData.map((item, index) => {
            if (index === props?.groupIndex) {
              return { ...item, quantity: Number(newValue) ?? 0 };
            }
            return item;
          });
          setEditModeBundleData(updatedBundles);
        } else if (newValue) {
          const updatedBundles = editModeBundleData.map((item, index) => {
            if (index === props?.groupIndex) {
              return { ...item, quantity: Number(newValue) };
            }
            return item;
          });
          const updatedBundleItems = editModeBundleItemData.map(
            (item, index) => {
              if (index >= startIndex && index < startIndex + count) {
                return {
                  ...item,
                  quantity: Number(newValue),
                  unitPrice: item.msrp,
                  netAmount: Number((item.msrp * Number(newValue)).toFixed(2)),
                  authorizedDiscount: 0.0,
                  authDiscountAmounts: [],
                  authDiscountPercentages: [],
                  discountReason: "",
                  discountedPercentage: 0,
                  lineItemIndex: index,
                };
              }
              return item;
            }
          );

          setEditModeBundleData(updatedBundles);
          setEditModeBundleItemData(updatedBundleItems);
        }
      };
      return (
        <Stack verticalFill verticalAlign="start" tokens={stackTokensWithGap10}>
          {defaultRender && defaultRender(props)}
          <Stack
            horizontal
            horizontalAlign="start"
            style={{ alignItems: "center" }}
            tokens={stackTokensWithGap10}
          >
            <h3>Quantity</h3>
            <TextField
              type="number"
              readOnly={!isEditing}
              ariaLabel="Quantity"
              errorMessage={quantity <= 0 ? "Invalid Quantity" : ""}
              styles={isEditing ? TextFieldStyles : TextGrayFieldStyles}
              value={quantity.toString()}
              onChange={customInputOnChange}
            />
          </Stack>
        </Stack>
      );
    }
    return null;
  };

  const renderBundleItemDetailsComponent = (): React.ReactElement => {
    return (
      <Stack>
        <div
          style={{
            height: "350px",
          }}
        >
          <CoherenceDataGrid
            groups={bundle}
            groupProps={{
              showEmptyGroups: false,
              onRenderHeader: onRenderGroupHeader,
            }}
            ariaLabelForSelectAllCheckbox="select all checkbox"
            checkButtonAriaLabel="checkbox"
            compactMode="compact"
            data={
              isEditing ? editModeBundleItemData : quotationInfo.bundleItems
            }
            onRenderDetailsHeader={onRenderDetailsHeader}
            isLoading={bundleItemLoading}
            isScrollable
            listConfig={[
              {
                fieldName: "sapItemId",
                maxWidth: 100,
                isResizable: true,
                key: "skuId",
                name: "SKU",
                type: "string",
              },
              {
                fieldName: "productTitle",
                isResizable: true,
                isMultiline: true,
                key: "productTitle",
                name: "Product Name",
                type: "string",
              },
              {
                fieldName: "availableInventory",
                isResizable: true,
                key: "availableInventory",
                name: "Available Inventory",
                type: "string",
              },
              {
                fieldName: "quantity",
                isResizable: true,
                key: "quantity",
                name: "Quantity",
                type: "number",
                isUpdated: true,
                displayFullValue: true,
                customFooterTitle: "",
              },
              {
                fieldName: "discountedPercentage",
                isResizable: true,
                key: "Discount Percentage",
                name: "Discount Percentage",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {item.discountedPercentage}%
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: "isPreOrder",
                isResizable: true,
                key: "Is Pre Order",
                name: "Is Pre Order",
                type: "string",
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {item.isPreOrder ? YES : NO}
                      </span>
                    </Stack>
                  );
                },
              },
              {
                fieldName: "msrp",
                isResizable: true,
                key: "MSRP",
                name: "MSRP",
                type: "number",
                showTotal: false,
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.msrp)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: "authorizedDiscount",
                isResizable: true,
                key: "Authorized Discount Amount",
                name: "Authorized Discount Amount",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.authorizedDiscount)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: "unitPrice",
                isResizable: true,
                key: "Unit Best Price",
                name: "Unit Best Price",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.unitPrice)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: `netAmount`,
                isResizable: true,
                key: "Best Price",
                name: "Best Price",
                type: "number",
                displayFullValue: true,
                customFooterTitle: "",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {formattedPrice(item.netAmount)}
                      </span>
                    </Stack>
                  )
                }
              },
              {
                fieldName: `discountReason`,
                isResizable: true,
                isMultiline: true,
                key: "Discount Reason",
                name: "Discount Reason",
                type: "string",
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {item.discountReason && item.discountReason.length > 0
                          ? item.discountReason
                          : "--"}
                      </span>
                    </Stack>
                  );
                },
              },
            ]}
            selection={bundleSelection}
            selectionMode={SelectionMode.multiple}
            totalsRowTitleSettings={{
              title: "Total",
              tooltipMessage: "All total will appear here",
            }}
            setKey="BUNDLE_ITEM_DETAILS_DATA_GRID_SORTABLE_SELECTABLE"
          />
        </div>
      </Stack>
    );
  };

  const renderItemPanelComponent = (): React.ReactElement => {
    return (
      <div>
        <AddItemPanelComponent
          isItemPanelOpen={isItemPanelOpen}
          dismissItemPanel={dismissItemPanel}
        />
      </div>
    );
  };

  const renderAuthorizedDiscountPanelComponent = (): React.ReactElement => {
    return (
      <div>
        {isDiscountPanelOpen &&
          selectedGridData !== undefined &&
          selectedGridData?.length! > 0 && (
            <AuthorizedDiscountComponent
              selectedPivot={selectedPivot}
              isDiscountPanelOpen={isDiscountPanelOpen}
              dismissDiscountPanel={dismissDiscountPanel}
              selectedProduct={selectedGridData[0] as IExtendedItemEntity}
              selectedProductIndex={(selectedGridData[0] as IExtendedItemEntity).lineItemIndex}
              selection={itemSelection}
              setLoading={setItemTableLoading}
            />
          )}
        {isDiscountPanelOpen &&
          selectedBundleGridData !== undefined &&
          selectedBundleGridData?.items?.length! === 1 && (
            <AuthorizedDiscountComponent
              selectedPivot={selectedPivot}
              isDiscountPanelOpen={isDiscountPanelOpen}
              dismissDiscountPanel={dismissDiscountPanel}
              selectedProduct={
                selectedBundleGridData?.items[0] as IExtendedItemEntity
              }
              selectedProductIndex={
                selectedBundleGridData.items[0].lineItemIndex
              }
              selection={bundleSelection}
              setLoading={setBundleItemLoading}
            />
          )}
      </div>
    );
  };
  const onPivotSelectionChange = (
    item?: PivotItem,
    event?: React.MouseEvent<HTMLElement>| React.KeyboardEvent<HTMLElement>
  ) => {
    if (item?.props.headerText) {
      appInsights.trackEvent({
        name: `In Quotation.tsx, Clicked Pivot ${item.props.headerText} to filter and show quotations of selected status.`,
      });
      setSelectedPivot(item.props.headerText);
      setSelectedGridData([]);
      setSelectedBundleGridData({ items: [] });
    }
  };

  const renderPivots = (): JSX.Element => {
    return (
      <Pivot
        style={{ paddingLeft: "30px", paddingRight: "30px" }}
        aria-label={selectedPivot}
        onLinkClick={onPivotSelectionChange}
        selectedKey={selectedPivot}
      >
        {quotationInfo?.standaloneItems?.length && (
          <PivotItem headerText={DeviceType.Standalone} itemKey={DeviceType.Standalone}></PivotItem>
        )}
        {quotationInfo?.bundleItems?.length && (
          <PivotItem headerText={DeviceType.Bundle} itemKey={DeviceType.Bundle}></PivotItem>
        )}
      </Pivot>
    );
  };

  return (
    <Stack
      data-testid="quotation-lineitems-page"
      verticalFill
      verticalAlign="start"
    >
      {quotationWarningMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.warning}
        >
          {quotationWarningMessage.message}
        </MessageBar>
      )}
      {quotationWaitMessage.showMessage && (
        <Stack horizontal horizontalAlign="start" tokens={stackTokensWithGap20}>
          <MessageBar messageBarType={MessageBarType.warning}>
            <Stack horizontal horizontalAlign="start" tokens={stackTokensWithGap20}>
              <Spinner size={SpinnerSize.medium} style={{ paddingRight: 10 }} />
              {quotationWaitMessage.message}
            </Stack>
          </MessageBar>
        </Stack>
      )}
      {quotationSuccessMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() =>
            setQuotationSuccessMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationSuccessMessage.message}
        </MessageBar>
      )}
      {quotationFailureMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() =>
            setQuotationFailureMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationFailureMessage.message}
        </MessageBar>
      )}
      <Stack tokens={stackTokensWithGap10}>
        <Breadcrumb
          items={getBreadcrumbItems()}
          style={{ marginLeft: "1%" }}
          styles={BreadcrumbStyle}
        />
        <Stack
          verticalFill
          verticalAlign="start"
          tokens={stackTokensWithGap10}
          style={{ marginLeft: "3%" }}
        >
          <Stack
            horizontal
            horizontalAlign={
              location?.state?.actionName === EDIT_ACTION ? "space-between" : "end"
            }
            style={{ marginRight: "3%" }}
          >
            {quotationInfo?.quotationNumber && quotationInfo.customer?.name && (
              <Label style={TextFont14}>
                {`${quotationInfo?.quotationNumber} - ${quotationInfo?.customer?.name}`}
              </Label>
            )}
            {quotationInfo?.total && (
              <Label style={TextFont18}>
                {priceWithCurrency(
                  quotationInfo.quoteCurrency,
                  quotationInfo.total
                )}
              </Label>
            )}
          </Stack>
          <Stack>{renderCustomCommandBar()}</Stack>
        </Stack>
      </Stack>
      <Stack style={{ marginLeft: "3%", marginRight: "3%" }}>
        <Separator />
      </Stack>
      <Stack>{renderPivots()}</Stack>
      {selectedPivot === DeviceType.Standalone &&
        quotationInfo.standaloneItems.length > 0 && (
          <Stack style={{ marginLeft: "4%", marginRight: "4%" }}>
            {renderStandaloneItemDetailsComponent()}
          </Stack>
        )}
      {selectedPivot === DeviceType.Bundle &&
        quotationInfo.bundleItems.length > 0 && (
          <Stack style={{ marginLeft: "4%", marginRight: "4%" }}>
            {renderBundleItemDetailsComponent()}
          </Stack>
        )}

      <Stack>{renderItemPanelComponent()}</Stack>
      <Stack>{renderAuthorizedDiscountPanelComponent()}</Stack>
      <Stack
        horizontal
        horizontalAlign="center"
        tokens={stackTokensWithGap30}
        style={{ marginTop: "30px" }}
      >
        <PrimaryButton
          text="Back"
          type="button"
          disabled={isLoading}
          style={{
            backgroundColor: isLoading
              ? "1px solid rgb(0, 120, 212)"
              : "1px solid lightgrey",
          }}
          onClick={() => {
            navigate(-1);
          }}
        />
        <PrimaryButton
          text="Calculate & Save"
          type="button"
          disabled={
            isLoading ||
              ((location?.state?.actionName === ADD_ACTION || location?.state?.actionName === NEW_ACTION) &&
                quotationInfo.standaloneItems.length === 0 &&
                quotationInfo.bundleItems.length === 0)
              ? true
              : false
          }
          style={{
            backgroundColor: isLoading
              ? "1px solid rgb(0, 120, 212)"
              : "1px solid lightgrey",
          }}
          onClick={_onClickSaveButton}
        />
        <PrimaryButton
          text="Cancel"
          type="button"
          disabled={isLoading}
          style={{
            backgroundColor: isLoading
              ? "1px solid rgb(0, 120, 212)"
              : "1px solid lightgrey",
          }}
          onClick={() => {
            setIsLoading(true);
            resetQuotationForm(
              quotationInfo,
              setQuotationInfo,
              setQuotationError
            );
            appInsights.trackEvent({
              name: "In QuotationItems.tsx, Clicked Add Line Items button to trigger redirect to Item Details screen.",
              properties: {
                quotationNumber: quotationInfo?.quotationNumber,
              },
            });
            setIsLoading(false);
            navigate("/AllQuotations");
          }}
        />
      </Stack>
    </Stack>
  );
};
